import React, { useState, useEffect } from 'react';
import * as tf from '@tensorflow/tfjs';

/* ==================================================
* TEMEL İSTATİSTİK YARDIMCILARI
* ================================================== */
const ort = (a) => a.reduce((s, v) => s + v, 0) / a.length;
const stdev = (a) => {
  if (a.length <= 1) return 0;
  const m = ort(a);
  return Math.sqrt(a.reduce((s, v) => s + (v - m) ** 2, 0) / (a.length - 1));
};
const slope = (y1, y0) => y1 - y0;
const pct   = (v1, v0) => ((v1 - v0) / v0) * 100;

/* ==================================================
* TEST-1…8 ALGILAYICILARI
* ================================================== */
// Test-1 ±3σ dışında ≥1 nokta
const threeSigmaPoints = (arr, sigma = 3) => {
  const m = ort(arr), s = stdev(arr);
  return arr.map((v, i) => (Math.abs(v - m) > sigma * s ? i + 1 : null)).filter(Boolean);
};

// Test-2 aynı tarafta 9 ardışık nokta
const nineSameSide = (arr, need = 9) => {
  const m = ort(arr), res = [];
  let run = 0, side = null;
  for (let i = 0; i < arr.length; i++) {
    const currSide = arr[i] > m;
    if (arr[i] === m) { run = 0; side = null; continue; }
    if (currSide === side) run++; else { run = 1; side = currSide; }
    if (run === need) res.push([i - need + 2, i + 1]);
    if (run > need)  res.at(-1)[1] = i + 1;
  }
  return res;
};

// Test-3 art arda 6 yükselen / azalan
const sixTrendRuns = (arr, need = 6) => {
  const res = []; let inc = 1, dec = 1;
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] > arr[i - 1]) { inc++; dec = 1; }
    else if (arr[i] < arr[i - 1]) { dec++; inc = 1; }
    else { inc = 1; dec = 1; }
    if (inc === need) res.push([i - need + 2, i + 1, 'yükselen']);
    if (dec === need) res.push([i - need + 2, i + 1, 'azalan']);
  }
  return res;
};

// Test-4 14 nokta ↑↓ alternasyonu
const alternatingRuns = (arr, need = 14) => {
  const res = []; let run = 1;
  for (let i = 2; i < arr.length; i++) {
    const d1 = arr[i - 1] - arr[i - 2];
    const d2 = arr[i]     - arr[i - 1];
    if (d1 * d2 < 0) {
      run++;
      if (run === need) res.push([i - need + 2, i + 1]);
      else if (run > need) res.at(-1)[1] = i + 1;
    } else run = 1;
  }
  return res;
};

// Test-5 3’te 2 nokta ±2σ dışında (aynı taraf)
const twoOfThreeBeyond2Sigma = (arr) => {
  const m = ort(arr), s = stdev(arr), res = [];
  for (let i = 2; i < arr.length; i++) {
    const win = arr.slice(i - 2, i + 1);
    const beyond = win.filter((v) => Math.abs(v - m) > 2 * s);
    if (beyond.length >= 2) {
      const allUp = beyond.every((v) => v > m);
      const allDn = beyond.every((v) => v < m);
      if (allUp || allDn) res.push([i - 1, i + 1]);
    }
  }
  return res;
};

// Test-6 5’te 4 nokta ±1σ dışında (aynı taraf)
const fourOfFiveBeyond1Sigma = (arr) => {
  const m = ort(arr), s = stdev(arr), res = [];
  for (let i = 4; i < arr.length; i++) {
    const win = arr.slice(i - 4, i + 1);
    const beyond = win.filter((v) => Math.abs(v - m) > 1 * s);
    const up = beyond.filter((v) => v > m).length;
    const dn = beyond.filter((v) => v < m).length;
    if (up >= 4 || dn >= 4) res.push([i - 3, i + 1]);
  }
  return res;
};

// Test-7 ±1σ içinde ardışık 15 nokta
const fifteenWithin1Sigma = (arr, need = 15) => {
  const m = ort(arr), s = stdev(arr), res = [];
  let run = 0, start = 0;
  for (let i = 0; i < arr.length; i++) {
    if (Math.abs(arr[i] - m) < s) {
      if (run === 0) start = i;
      run++;
      if (run === need) res.push([start + 1, i + 1]);
      else if (run > need) res.at(-1)[1] = i + 1;
    } else run = 0;
  }
  return res;
};

// Test-8 ±1σ DIŞINDA ardışık 8 nokta (her iki tarafta min 1)
const eightOutside1Sigma = (arr, need = 8) => {
  const m = ort(arr), s = stdev(arr), res = [];
  if (!s) return res;
  for (let i = need - 1; i < arr.length; i++) {
    const win = arr.slice(i - need + 1, i + 1);
    if (win.every((v) => Math.abs(v - m) > s)) {
      const up = win.some((v) => v > m);
      const dn = win.some((v) => v < m);
      if (up && dn) res.push([i - need + 2, i + 1]);
    }
  }
  return res;
};

/* ==================================================
* EK GÖZLEM & ANALİZ FONKSİYONLARI
* ================================================== */
const findIdenticalRuns = (arr, need = 4) => {
  const res = []; let run = 1;
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] === arr[i - 1]) {
      run++;
      if (run >= need && (i === arr.length - 1 || arr[i + 1] !== arr[i]))
        res.push([i - run + 2, i + 1]);
    } else run = 1;
  }
  return res;
};
const cycleIndic = (arr) => {
  const m = ort(arr); let cross = 0, above = arr[0] > m;
  for (let i = 1; i < arr.length; i++) {
    const now = arr[i] > m;
    if (now !== above) { cross++; above = now; }
  }
  return cross >= 4;
};
const detectTrendSegments = (arr, { minLen = 4, minSlope = 0 }) => {
  const segs = []; let run = 1, dir = 0, start = 0;
  for (let i = 1; i < arr.length; i++) {
    const d = slope(arr[i], arr[i - 1]);
    const cur = d > minSlope ? 1 : d < -minSlope ? -1 : 0;
    if (cur && cur === dir) run++;
    else {
      if (run >= minLen && dir) segs.push([start + 1, i, dir === 1 ? 'yükselen' : 'azalan']);
      run = cur ? 2 : 1; start = i - 1;
    }
    dir = cur;
  }
  if (run >= minLen && dir) segs.push([start + 1, arr.length, dir === 1 ? 'yükselen' : 'azalan']);
  return segs;
};
const findLocalExtrema = (arr) => {
  const peaks = [], troughs = [];
  for (let i = 1; i < arr.length - 1; i++) {
    if (arr[i] > arr[i - 1] && arr[i] > arr[i + 1]) peaks.push(i + 1);
    if (arr[i] < arr[i - 1] && arr[i] < arr[i + 1]) troughs.push(i + 1);
  }
  return { peaks, troughs };
};
const suddenJumps = (arr, th = 5) => {
  const res = [];
  for (let i = 1; i < arr.length; i++) {
    const change = pct(arr[i], arr[i - 1]);
    if (Math.abs(change) >= th)
      res.push([i, i + 1, change > 0 ? 'sıçrama' : 'düşüş', change.toFixed(1)]);
  }
  return res;
};
const varianceSpike = (arr, win = 6, ratio = 1.5) => {
  if (arr.length < win * 2) return false;
  const s1 = stdev(arr.slice(-2 * win, -win));
  const s2 = stdev(arr.slice(-win));
  return s1 && s2 && s2 / s1 >= ratio;
};
const cusumShift = (arr, k = 0.5, h = 5) => {
  const m = ort(arr), s = stdev(arr);
  if (!s) return [];
  let cp = 0, cm = 0, res = [];
  for (let i = 0; i < arr.length; i++) {
    const z = (arr[i] - m) / s;
    cp = Math.max(0, cp + z - k);
    cm = Math.max(0, cm - z - k);
    if (cp > h) { res.push([i + 1, 'üst']);  cp = 0; }
    if (cm > h) { res.push([i + 1, 'alt']); cm = 0; }
  }
  return res;
};
const acfLag1 = (arr, len = 12) => {
  if (arr.length < len + 1) return false;
  const seg = arr.slice(-len), m = ort(seg);
  const num = seg.slice(1).reduce((s, v, i) => s + (v - m) * (seg[i] - m), 0);
  const den = seg.reduce((s, v) => s + (v - m) ** 2, 0);
  return den && num / den > 0.6;
};
const seasonalityHint = (arr) => {
  if (arr.length < 8) return false;
  const { peaks, troughs } = findLocalExtrema(arr);
  const seq = [...peaks.map(i => [i,'P']), ...troughs.map(i => [i,'T'])]
    .sort((a,b) => a[0]-b[0]).slice(-6).map(([,t])=>t).join('');
  return /^(P?T?P?T?P?T?|T?P?T?P?T?P?)$/.test(seq) && seq.length >= 6;
};

/* ==================================================
* REACT BİLEŞENİ
* ================================================== */
const GraphAI = ({ analysisData }) => {
  const [tahmin, setTahmin] = useState('');
  const [yorum,  setYorum]  = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { cp = 1, cpk = 1, outCount = 0, xbars = [] } = analysisData;

        // Küçük ML tahmini
        const model = tf.sequential();
        model.add(tf.layers.dense({ inputShape: [3], units: 16, activation: 'relu' }));
        model.add(tf.layers.dense({ units: 16, activation: 'relu' }));
        model.add(tf.layers.dense({ units: 3, activation: 'softmax' }));
        model.compile({ loss: 'categoricalCrossentropy', optimizer: 'adam' });
        await model.fit(
          tf.tensor2d([[1.3,1.2,0],[0.7,0.6,3],[1.0,0.9,1]]),
          tf.tensor2d([[1,0,0],[0,1,0],[0,0,1]]),
          { epochs: 30 }
        );
        const pred = model.predict(tf.tensor2d([[cp,cpk,outCount]])).arraySync()[0];
        const mlLabel = ['Kararlı','Kontrol Dışı','Uyarı'][pred.indexOf(Math.max(...pred))];

        const msg = []; const add = (t) => msg.push(t);

        // Test 1
        const t1 = threeSigmaPoints(xbars);
        t1.length
          ? add(`❌ Test 1 – ±3σ dışı noktalar: ${t1.join(', ')} (veri beklenenden fazla sapıyor; proses ayarlarını kontrol edin)`)
          : add(`✅ Test 1 – ±3σ dışı nokta yok (normal sınırda; izlemeye devam edin)`);

        // Test 2
        const t2 = nineSameSide(xbars);
        t2.length
          ? t2.forEach(r => add(`🚨 Test 2 – ${r[0]}-${r[1]} arası 9 nokta aynı tarafta (olası kayma; dengeyi gözden geçirin)`))
          : add(`✅ Test 2 – 9 ardışık aynı tarafta yok (proses dengeli)`);

        // Test 3
        const t3 = sixTrendRuns(xbars);
        t3.length
          ? t3.forEach(r => add(`🚨 Test 3 – ${r[0]}-${r[1]} sürekli ${r[2]} (tutarlı eğilim; nedeni araştırın)`))
          : add(`✅ Test 3 – 6’lı trend yok (rastgele dalgalanma)`);

        // Test 4
        const t4 = alternatingRuns(xbars);
        t4.length
          ? t4.forEach(r => add(`⚠️ Test 4 – ${r[0]}-${r[1]} ↑↓ alternasyonu (dalga var; operatör ayarlarını inceleyin)`))
          : add(`✅ Test 4 – Alternasyon yok (dalgalanma kontrol altında)`);

        // Test 5
        const t5 = twoOfThreeBeyond2Sigma(xbars);
        t5.length
          ? t5.forEach(r => add(`⚠️ Test 5 – ${r[0]}-${r[1]} pencerede 3’te 2 nokta ±2σ dışı (aşırı varyasyon; dikkat)`))
          : add(`✅ Test 5 – 3’te 2 dışı varyasyon yok (normal)`);

        // Test 6
        const t6 = fourOfFiveBeyond1Sigma(xbars);
        t6.length
          ? t6.forEach(r => add(`⚠️ Test 6 – ${r[0]}-${r[1]} pencerede 5’te 4 nokta ±1σ dışı (küçük sapmalar; izleyin)`))
          : add(`✅ Test 6 – 5’te 4 varyasyon yok (kontrol altında)`);

        // Test 7
        const t7 = fifteenWithin1Sigma(xbars);
        t7.length
          ? t7.forEach(r => add(`ℹ️ Test 7 – ${r[0]}-${r[1]} arası 15 nokta ±1σ bandında (çok stabil; aralığı genişletmeyi düşünün)`))
          : add(`✅ Test 7 – 15 ardışık nokta ±1σ bandında değil (normal çeşitlilik)`);

        // Test 8
        const t8 = eightOutside1Sigma(xbars);
        t8.length
          ? t8.forEach(r => add(`ℹ️ Test 8 – ${r[0]}-${r[1]} arası 8 nokta ±1σ dışı (hem yukarı hem aşağı; dengeyi kontrol edin)`))
          : add(`✅ Test 8 – ±1σ dışı ardışık 8 nokta yok (istem dışı değişim yok)`);

        // Ek bulgular
        findIdenticalRuns(xbars).forEach(r =>
          add(`🔒 4+ ardışık aynı değer (${r[0]}-${r[1]}) (ölçüm tekrar; sensör kontrolü)`));
        if (cycleIndic(xbars))
          add('🔄 Ortalama çizgisi ≥4 kez kesilmiş (döngüsel dalgalanma; periyodik inceleme)');

        detectTrendSegments(xbars, { minLen: 4 }).forEach(s =>
          add(`📈 Sürekli ${s[2]} eğilimi (${s[0]}-${s[1]}) (kalıcı değişim; müdahale gerekebilir)`));

        const { peaks, troughs } = findLocalExtrema(xbars);
        peaks.length   && add(`🔺 Yerel tepe: ${peaks.join(', ')} (anlık maksimumlar)`);
        troughs.length && add(`🔻 Yerel dip: ${troughs.join(', ')} (anlık minimumlar)`);

        suddenJumps(xbars).forEach(j =>
          add(`⚡ Ani ${j[2]} ${j[3]}% (${j[0]}→${j[1]}) (hızlı değişim; kontrol edin)`));

        if (varianceSpike(xbars)) add('📈 Varyans sıçraması son pencerede (dalgalanma artışı; dikkat)');

        cusumShift(xbars).forEach(c =>
          add(`📉/📈 CUSUM kayması (nokta ${c[0]} ${c[1]}) (kümülatif sapma; koreksiyon önerilir)`));

        if (acfLag1(xbars)) add('🔄 Lag-1 otokorelasyon > 0.6 (gecikmeli bağımlılık)');
        if (seasonalityHint(xbars)) add('🎯 Düzenli tepe-dip dizisi (mevsimsellik olabilir)');

        // Özet
        const cap = cp >= 1.33 && cpk >= 1.33 ? 'İyi'
                  : cp < 1 || cpk < 1         ? 'Yetersiz'
                  : 'Sınırda';

        setTahmin(mlLabel);
        setYorum(
          [
            `• Cp: ${cp.toFixed(2)}, Cpk: ${cpk.toFixed(2)} → **${cap}**`,
            `• ML Tahmini: **${mlLabel}**`,
            ...msg
          ].join('\n')
        );
      } catch (err) {
        console.error('GraphAI error:', err);
        setTahmin('Hata');
        setYorum('AI analizinde hata oluştu.');
      }
    })();
  }, [analysisData]);

  return (
    <div style={{ marginTop: 10 }}>
      <h4>Grafik AI Analizi</h4>
      <p><strong>Tahmin:</strong> {tahmin}</p>
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{yorum}</pre>
    </div>
  );
};

export default GraphAI;
